import { OrderDiscountOffer, NewDiscountType } from '@baemingo/offer';
import { ChequeDiscount } from '@baemingo/table-events/dist/types';
import { MEMBERSHIP_DISCOUNT_ID } from '~Components/LinkUser/consts';
import { DatadogLogger } from '~Hooks/Entities/useLogger';
import { LocalItem } from '~Types';
import {
  GetOffers_Company_offers,
  GetOffers_Company_allPromoCodes,
} from '~Hooks/__queries__';
import { DISCOUNT_TYPE } from '../../../__queries__/globalTypes';
import { BOOKINGS_DISCOUNT_ID } from '~Providers/BookingsProvider';
import { MANUAL_DISCOUNT_ID } from '~Components/DiscountButton';

// Map old DISCOUNT_TYPE to new NewDiscountType
export const DISCOUNT_TYPE_MAP: Record<DISCOUNT_TYPE, NewDiscountType> = {
  [DISCOUNT_TYPE.FIXED]: NewDiscountType.FIXED,
  [DISCOUNT_TYPE.PERCENTAGE]: NewDiscountType.PERCENTAGE,
  [DISCOUNT_TYPE.PRICE]: NewDiscountType.RELATIVE,
};

/** Takes in the discount set on the cheque & returns how much discount each item should get when added to the cheque */
export const getPersistentDiscounts = (
  persistentDiscount: ChequeDiscount,
  items: LocalItem[],
  offers: GetOffers_Company_offers[],
  promoCodeById: Record<string, GetOffers_Company_allPromoCodes>,
  logger: DatadogLogger,
  bookingDiscountCategoryIds: string[],
  discountExcludedCategoryIds: string[] = [],
): Map<string, number> => {
  const discountMap = new Map<string, number>();

  const foundOffer = offers.find((offer) => offer.id === persistentDiscount.id);
  const foundPromoCode = promoCodeById[persistentDiscount.id];

  if (
    !foundOffer &&
    !foundPromoCode &&
    persistentDiscount.id !== BOOKINGS_DISCOUNT_ID &&
    persistentDiscount.id !== MEMBERSHIP_DISCOUNT_ID &&
    persistentDiscount.id !== MANUAL_DISCOUNT_ID
  ) {
    logger.warn(
      'Tried to read an offer from cache, but it did not exist in cache. This may be due to the offer failed to fetch on boot or the offer being deleted after it has been applied to a cheque.',
      {
        offerId: persistentDiscount.id,
      },
    );
    return discountMap;
  }

  /**
   * Normalize the fields we need from either an offer or a promo code, since
   * they expose the item/category restrictions differently. Offers restrict
   * both to specific items and categories, while promo codes only restrict
   * to categories.
   */
  const discountRestrictions = foundOffer
    ? {
        allowedCategoryIds: foundOffer.categories.map((c) => c.id),
        allowedItemIds: foundOffer.items.map((i) => i.id),
        itemIds: [
          ...foundOffer.items.map((item) => item.id),
          ...foundOffer.categories.flatMap((cat) => cat.items.map((item) => item.id)),
        ],
      }
    : foundPromoCode
    ? {
        allowedCategoryIds: foundPromoCode.categoryIds,
        allowedItemIds: [] as string[],
        itemIds: [] as string[],
      }
    : { allowedCategoryIds: [] as string[], allowedItemIds: [] as string[], itemIds: [] as string[] };

  // Extract itemIds from the offer (direct items + category items)
  const itemIds = discountRestrictions.itemIds;

  // Create OrderDiscountOffer with constructor parameters (new API)
  const offer = new OrderDiscountOffer({
    discountType: DISCOUNT_TYPE_MAP[persistentDiscount.type],
    discountRate: persistentDiscount.rate,
    allowedCategoryIds:
      persistentDiscount.id === BOOKINGS_DISCOUNT_ID
        ? [] // Booking discount uses blacklist filtering below instead
        : discountRestrictions.allowedCategoryIds,
    allowedItemIds: discountRestrictions.allowedItemIds,
  });

  // Only add items that are included in the offer's itemIds (or all if no restriction)
  items.forEach((item) => {
    if (itemIds.length > 0 && !itemIds.includes(item.itemId)) {
      return;
    }

    // For booking discounts, skip items in blacklisted categories
    if (
      persistentDiscount.id === BOOKINGS_DISCOUNT_ID &&
      bookingDiscountCategoryIds.length > 0 &&
      bookingDiscountCategoryIds.includes(item.categoryId)
    ) {
      return;
    }

    // Skip items in venue-level discount excluded categories (applies to all discount types)
    if (
      discountExcludedCategoryIds.length > 0 &&
      discountExcludedCategoryIds.includes(item.categoryId)
    ) {
      return;
    }

    offer.addItem({
      id: item.id,
      itemId: item.itemId,
      categoryId: item.categoryId,
      amount: item.amount || 0,
    });
  });

  // Use the new calculate() API
  const { changedItems } = offer.calculate();

  changedItems.forEach((d) => {
    discountMap.set(d.id, d.discount);
  });

  return discountMap;
};
